// Conversational AI Agent - AI-only natural language interface for GTM agents

import { gtmAgentService } from './agentService';
import { claudeService } from './claudeService';
import { AgentExecutionResult } from './agentTypes';
import realDataService from '../realDataService';
import { leadsStore } from '../../stores/leadsStore';
import NetlifyAIService from './netlifyAIService';

export interface ConversationContext {
  userId: string;
  sessionId: string;
  conversationHistory: ConversationMessage[];
  lastTask?: string;
  extractedEntities?: Record<string, any>;
}

export interface ConversationMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  intent?: string;
  entities?: Record<string, any>;
  agentResult?: AgentExecutionResult;
}

export interface ConversationResponse {
  message: string;
  intent: string;
  confidence: number;
  suggestedActions?: string[];
  agentResult?: AgentExecutionResult;
  followUpQuestions?: string[];
}

class ConversationalAgent {
  private useClaudeAPI = true; // AI-only mode always uses Claude
  private netlifyAI?: NetlifyAIService;
  private dataValidationEnabled = true;

  // AI-only mode - no rule-based patterns needed

  // AI-only mode - entity extraction handled by Claude

  async processMessage(
    userInput: string,
    context: ConversationContext
  ): Promise<ConversationResponse> {
    // REMOVED: Special lead handling with regex patterns - Let Claude AI handle everything

    // SKIP PRE-VALIDATION - Let Claude AI handle all interpretation naturally
    console.log(`User input: "${userInput}" - sending directly to Claude AI`);

    // Try Claude API for other requests if enabled
    if (this.useClaudeAPI) {
      try {
        const claudeResponse = await claudeService.generateConversationalResponse(userInput, context);

        // If Claude detected a specific actionable task, execute it
        if (claudeResponse.intent === 'leadQualification' && this.containsLeadInfo(userInput)) {
          const entities = this.extractEntities(userInput);
          const agentResult = await this.executeLeadQualification(userInput, entities, context);
          if (agentResult) {
            claudeResponse.agentResult = agentResult;
          }
        }

        return claudeResponse;
      } catch (error) {
        console.error('Claude API failed - AI-only mode requires functional AI service:', error);
        return {
          message: `I'm having trouble connecting to the AI service right now. Please check your AI configuration and try again. Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          intent: 'error',
          confidence: 0,
          suggestedActions: [] // Removed suggestions
        };
      }
    }

    // If Claude API is disabled, return error since we're in AI-only mode
    return {
      message: 'AI-only mode is enabled but Claude API is disabled. Please enable the Claude API to use the conversational agent.',
      intent: 'configuration_error',
      confidence: 100,
      suggestedActions: [] // Removed suggestions
    };
  }

  // Helper methods for special case handling
  private containsPossibleName(input: string): boolean {
    // Simple check for potential names (two capitalized words)
    return /\b[A-Z][a-z]+\s+[A-Z][a-z]+\b/.test(input);
  }

  private containsLeadInfo(input: string): boolean {
    const leadIndicators = ['email', '@', 'company', 'prospect', 'contact', 'lead'];
    return leadIndicators.some(indicator => input.toLowerCase().includes(indicator));
  }

  private extractEntities(userInput: string): Record<string, any> {
    // Simplified entity extraction - in AI-only mode, Claude does most of this
    const entities: Record<string, any> = {};

    // Extract email addresses
    const emailMatch = userInput.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/);
    if (emailMatch) {
      entities.email = emailMatch[0];
    }

    return entities;
  }

  private async handleIndividualLeadLookup(
    userInput: string,
    context: ConversationContext
  ): Promise<ConversationResponse> {
    try {
      const leads = leadsStore.getLeads();
      if (leads.length === 0) {
        return {
          message: "No leads found in the dashboard. Please load some lead data first.",
          intent: 'no_data',
          confidence: 100
        };
      }

      // Use AI for prioritization with actual lead data
      const prioritizedLeads = await realDataService.getRecentLeads();
      const topLeads = prioritizedLeads.slice(0, 5);

      let response = `🎯 TOP PRIORITY LEADS FROM DEMO DATA (analyzed ${prioritizedLeads.length} sample leads):\n\n`;

      topLeads.forEach((lead, index) => {
        const score = Math.floor(Math.random() * 30) + 60; // Demo scoring
        response += `${index + 1}. ${lead.name} ⭐\n`;
        response += `   Company: ${lead.company}\n`;
        response += `   Score: ${score}/100\n`;
        response += `   Status: ${score >= 80 ? 'hot' : score >= 60 ? 'warm' : 'cold'}\n`;
        response += `   Value: $${lead.value.toLocaleString()}\n`;
        response += `   Email: ${lead.email}\n`;
        response += `   Key Insight: Strong buying intent signals detected\n\n`;
      });

      response += `📞 DEMO RECOMMENDATION: In a real scenario, start with ${topLeads[0]?.name} from ${topLeads[0]?.company} - they have the highest qualification score in this sample data!`;

      return {
        message: response,
        intent: 'leadPrioritization',
        confidence: 0.95,
        suggestedActions: [], // Removed suggestions
        followUpQuestions: [] // Removed follow-up questions
      };
    } catch (error) {
      console.error('Lead prioritization failed:', error);
      return {
        message: "I'm having trouble accessing lead data right now. Please try again.",
        intent: 'error',
        confidence: 0
      };
    }
  }

  private async executeLeadQualification(
    userInput: string,
    entities: Record<string, any>,
    context: ConversationContext
  ): Promise<AgentExecutionResult | null> {
    try {
      // Use AI-powered lead qualification
      if (!this.netlifyAI) {
        this.netlifyAI = new NetlifyAIService();
      }

      // Create a mock lead from extracted entities for qualification
      const mockLead = this.createLeadFromInput(userInput, entities);
      const enhancedScores = await this.netlifyAI.scoreLeadsEnhanced([mockLead]);
      const result = enhancedScores[0];

      return {
        success: true,
        result: {
          score: result.score,
          qualification: result.score >= 80 ? 'hot' : result.score >= 60 ? 'warm' : 'cold',
          confidence: result.confidenceScore,
          provider: result.aiProviderUsed,
          analysis: result.enrichmentAnalysis
        },
        executionTime: Date.now() - Date.now(), // Mock timing
        agent: 'leadQualifierAgent'
      };
    } catch (error) {
      console.error('AI lead qualification failed:', error);
      return null;
    }
  }

  private createLeadFromInput(userInput: string, entities: Record<string, any>): any {
    // Create a basic lead structure from user input for AI analysis
    const lead: any = {
      id: 'temp-' + Date.now(),
      name: 'Unknown',
      company: 'Unknown',
      email: entities.email || 'unknown@example.com',
      phone: '',
      status: 'New',
      source: 'Manual',
      value: 50000, // Default value
      createdAt: new Date().toISOString()
    };

    // Extract name if present
    const nameMatch = userInput.match(/\b([A-Z][a-z]+\s+[A-Z][a-z]+)\b/);
    if (nameMatch) {
      lead.name = nameMatch[1];
    }

    // Extract company if present
    const companyMatch = userInput.match(/\b([A-Z][a-zA-Z\s&]+(?:Inc|LLC|Corp|Company|Ltd|Corporation)\.?)\b/);
    if (companyMatch) {
      lead.company = companyMatch[1];
    }

    return lead;
  }

  private async validateDataReferences(userInput: string, context: ConversationContext): Promise<{exists: boolean, type: string, query: string}> {
    const input = userInput.toLowerCase();
    const leads = leadsStore.getLeads();

    if (leads.length === 0) {
      return { exists: true, type: '', query: '' }; // No data to validate against
    }

    // Check if this is asking about a specific person (common patterns)
    const personPatterns = [
      /tell me about ([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i,
      /who is ([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)\?*$/i // Name at end of question
    ];

    for (const pattern of personPatterns) {
      const match = userInput.match(pattern);
      if (match) {
        const name = match[1].trim();
        console.log(`Checking person name: "${name}"`);

        // Skip obvious non-names
        if (['Advanced Enterprises', 'Acme Corp', 'Strategic Labs', 'Premier Group'].includes(name)) {
          continue;
        }

        const nameExists = leads.some((lead: any) => {
          const leadName = lead.name?.toLowerCase() || '';
          const queryName = name.toLowerCase();
          const matches = leadName.includes(queryName) || queryName.includes(leadName);
          if (matches) {
            console.log(`Found match: "${leadName}" matches "${queryName}"`);
          }
          return matches;
        });

        console.log(`Name "${name}" exists: ${nameExists}`);

        if (!nameExists) {
          return {
            exists: false,
            type: 'person',
            query: name
          };
        }
      }
    }

    // Check for company references
    const companyPatterns = [
      /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*(?:\s+(?:Inc|Corp|Ltd|LLC|Company|Group|Solutions|Systems|Technologies|Enterprises)))\b/gi
    ];

    for (const pattern of companyPatterns) {
      const matches = userInput.match(pattern);
      if (matches) {
        for (const company of matches) {
          const cleanCompany = company.trim();
          if (['What Company', 'The Company'].includes(cleanCompany)) {
            continue;
          }

          const companyExists = leads.some((lead: any) =>
            lead.company?.toLowerCase().includes(cleanCompany.toLowerCase())
          );

          if (!companyExists) {
            return {
              exists: false,
              type: 'company',
              query: cleanCompany
            };
          }
        }
      }
    }

    return { exists: true, type: '', query: '' };
  }

  // REMOVED - This function should never be called anymore
  private generateDataNotFoundResponse(type: string, query: string): string {
    return `This function should not be called - regex validation was removed. Query: "${query}"`;
  }

  // Configuration and testing methods
  enableClaudeAPI(): void {
    this.useClaudeAPI = true;
  }

  disableClaudeAPI(): void {
    this.useClaudeAPI = false;
  }

  isClaudeAPIEnabled(): boolean {
    return this.useClaudeAPI;
  }

  async testClaudeConnection(): Promise<boolean> {
    try {
      return await claudeService.testConnection();
    } catch (error) {
      console.error('Claude connection test failed:', error);
      return false;
    }
  }
}

export const conversationalAgent = new ConversationalAgent();